<script setup lang="ts">
import { ref, computed, h, inject, Ref } from 'vue';
import { ColumnDef, SortingState } from '@tanstack/vue-table';
import { Plus, History } from 'lucide-vue-next';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';
import { format } from 'date-fns';
import MainViewCard from '@/components/common/MainViewCard.vue';
import Icon from '@/components/icons/Icon.vue';
import { IconSource, ProductionGroup, ProductionGroupBarnResponseEntry } from '@/types';
import { Checkbox } from '@/components/ui/checkbox';
import Badge from '@/components/ui/badge/Badge.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import DataTable from '@/components/ui/data-table/DataTable.vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { DataTableFacetFilter, type FacetSelection } from '@/components/ui/data-table';
import { queryKeys } from '@/plugins/store/actions/queries/production-groups';
import { durationDays, formatDuration } from './useProductionGroupBackfill';
import GroupFavoriteToggle from './GroupFavoriteToggle.vue';
import { useSectionById } from './useSectionById';

// Caps each Barn's own group table at this height, scrolling nested rows past a sticky header
// rather than growing the page - same treatment the records tables (Pigs/Slaughters) use for large
// row counts.
const MAX_BODY_HEIGHT = 'max-h-[60vh]';

const props = defineProps<{
    barn: ProductionGroupBarnResponseEntry;
    // Whether this Barn's Farm currently has a non-empty search query (Index.vue) - drives
    // auto-expanding Section tiles so a search match can never hide behind a collapsed one.
    searchActive: boolean;
    // `undefined` until the farmer picks a mode by hand. Owned by Index.vue (keyed by barn_id)
    // instead of locally: a species-toggle flip can drop this Barn out of `filteredData` entirely and
    // back, unmounting/remounting this component, which would discard a local ref's choice.
    manualViewMode?: 'all' | 'bySection';
}>();
const emit = defineEmits<{
    create: [barnId: number];
    edit: [group: ProductionGroup];
    // `sectionId` set when launched from a Section's own expanded panel (By-section view) rather
    // than the Barn-level button, so the wizard can pre-fill that Section instead of making the
    // farmer pick it again.
    backfill: [barnId: number, sectionId?: number];
    'update:manualViewMode': [mode: 'all' | 'bySection'];
}>();

// Groups overview is a management surface - name/dates/favorite/creation type, the facts a farmer
// edits or checks a group by - not a KPI readout. Growth index and Animal group are read-only
// figures that belong on Active groups' own per-Section view instead, so neither column nor the
// growth-interval selector that would drive it appears here.
//
// Explicit per-column widths, applied to both header and cell via `meta.headerClass`/`cellClass` and
// paired with `table-fixed` on the <table> (see template) - each Section renders its own independent
// DataTable instance, so without a fixed layout a long value in one Section's Name column (e.g. a
// backfilled group's auto-generated name) widens only that table, throwing its columns out of
// alignment with every other expanded Section's table above/below it. Name is the one column left
// unconstrained, so it absorbs whatever width table-fixed leaves over.
const COLUMN_WIDTH = {
    select: 'w-12',
    location: 'w-32',
    start: 'w-28',
    end: 'w-28',
    duration: 'w-24',
    creationType: 'w-36',
    favorite: 'w-16',
};

// Its own computed, not an inline entry in `columns` below, so `sectionColumns` can filter it out by
// reference identity - the By-section view repeats this same column set minus Location (every table
// is already scoped to one Section there), and a plain object identity check needs no cast into
// `ColumnDef`'s accessor/display/group union the way reading `.accessorKey` off it would.
const locationColumn = computed<ColumnDef<ProductionGroup & { _locationName: string }>>(() => ({
    header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.location'),
    accessorKey: '_locationName',
    enableSorting: true,
    meta: { headerClass: COLUMN_WIDTH.location, cellClass: COLUMN_WIDTH.location },
}));

const columns = computed<ColumnDef<ProductionGroup & { _locationName: string }>[]>(() => [
    {
        id: '_select',
        header: ({ table }) =>
            h(Checkbox, {
                checked: table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate'),
                'onUpdate:checked': (value) => table.toggleAllPageRowsSelected(!!value),
                ariaLabel: i18n.global.t('views.monitoring.groupOverview.fields.selectAll.label'),
                class: 'mt-1',
            }),
        cell: ({ row }) =>
            h(Checkbox, {
                checked: row.getIsSelected(),
                'onUpdate:checked': (value) => row.toggleSelected(!!value),
                ariaLabel: i18n.global.t('views.monitoring.groupOverview.fields.selectGroup.label'),
                class: 'mt-1',
                // Row itself opens Edit on click; the checkbox needs its own click kept local.
                onClick: (event: Event) => event.stopPropagation(),
            }),
        meta: { headerClass: COLUMN_WIDTH.select, cellClass: COLUMN_WIDTH.select },
    },
    locationColumn.value,
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.name'),
        accessorKey: 'name',
        enableSorting: true,
    },
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.startDate'),
        accessorKey: 'start',
        enableSorting: true,
        meta: { headerClass: COLUMN_WIDTH.start, cellClass: COLUMN_WIDTH.start },
    },
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.endDate'),
        accessorKey: 'end',
        enableSorting: true,
        meta: { headerClass: COLUMN_WIDTH.end, cellClass: COLUMN_WIDTH.end },
    },
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.duration'),
        accessorKey: '_duration',
        meta: { headerClass: COLUMN_WIDTH.duration, cellClass: COLUMN_WIDTH.duration },
    },
    {
        header: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.creationType'),
        accessorKey: 'is_auto_generated',
        enableSorting: true,
        meta: {
            align: 'center',
            headerClass: COLUMN_WIDTH.creationType,
            cellClass: COLUMN_WIDTH.creationType,
        },
    },
    {
        header: '',
        accessorKey: 'is_favorite',
        meta: { headerClass: COLUMN_WIDTH.favorite, cellClass: COLUMN_WIDTH.favorite },
    },
]);
const sorting = ref<SortingState>([
    {
        id: 'start',
        desc: true,
    },
]);

// Each per-Section table (see template, by-Section mode) gets its own independent sorting/page/
// pageSize, created once per Section id and cached here - NOT this component's single top-level
// `sorting`/`page`/`pageSize` above, which stays reserved for the flat ('all') table only. Several
// Section tables can be expanded at once (e.g. a search-driven mass expand), each mounting/updating
// its own DataTable in the same reactive flush; one shared ref read and written by all of them at
// once overflows Vue's recursive-update guard, so sort/page state must stay isolated per Section.
// Row selection stays intentionally shared (see `rowSelection` below, and the cross-Section "Manage
// selected" behaviour it drives) - only sort/page state needs isolating.
const sectionTableState = new Map<number, { sorting: Ref<SortingState>; page: Ref<number>; pageSize: Ref<number> }>();

function getSectionTableState(sectionId: number) {
    let state = sectionTableState.get(sectionId);

    if (!state) {
        state = {
            sorting: ref<SortingState>([{ id: 'start', desc: true }]),
            page: ref(1),
            pageSize: ref(9999),
        };
        sectionTableState.set(sectionId, state);
    }

    return state;
}

const sectionById = useSectionById();

function getSection(locationId: number) {
    return sectionById.value.get(locationId);
}

const queryClient = useQueryClient();
const toast = useToast();

// In flight by group id, so a second click on the same star while its request is still out doesn't
// fire a second overlapping edit (two in-flight toggles racing would leave the row showing whichever
// response lands last, not the farmer's actual last click).
const pendingFavoriteIds = ref<Set<number>>(new Set());

async function toggleFavorite(group: ProductionGroup) {
    if (pendingFavoriteIds.value.has(group.id)) {
        return;
    }

    pendingFavoriteIds.value = new Set(pendingFavoriteIds.value).add(group.id);

    try {
        // `group.start`/`group.end` are typed as `string` but arrive as already-parsed `Date`
        // objects (Index.vue's own `data` computed runs them through `parseISO` before handing
        // groups down to this component - see groupDuration's own `as unknown as Date` cast below).
        // Sent raw, a bare `JSON.stringify` turns them into full UTC timestamps, which the backend's
        // `DateOnly` binder rejects. Re-format to the plain yyyy-MM-dd it expects, same as the Manage
        // Group modal does for its own start/end fields.
        await store.dispatch(ActionType.EditProductionGroups, {
            options: { skipHandleError: true },
            form: {
                production_groups: [
                    {
                        id: group.id,
                        name: group.name,
                        note: group.note,
                        start: format(group.start as unknown as Date, 'yyyy-MM-dd'),
                        end: group.end ? format(group.end as unknown as Date, 'yyyy-MM-dd') : null,
                        is_favorite: !group.is_favorite,
                    },
                ],
            },
        });
        await queryClient.invalidateQueries({ queryKey: queryKeys.getProductionGroupsRoot });
    } catch {
        toast.error(i18n.global.t('views.monitoring.groupOverview.tables.barnTable.errors.couldNotUpdateFavorite'));
    } finally {
        const next = new Set(pendingFavoriteIds.value);
        next.delete(group.id);
        pendingFavoriteIds.value = next;
    }
}

// Days the group spans (inclusive of both ends) with the whole weeks they contain, formatted the
// same way the backfill wizard's own duration column does (useProductionGroupBackfill.ts) - calls
// its own durationDays rather than reimplementing the arithmetic, so the two can't disagree. Elapsed
// milliseconds between two real Date instants is not a reliable day count across a daylight-saving
// transition (a 23- or 25-hour local day still divides to 0 or still-incomplete-1 whole days); going
// through plain "yyyy-MM-dd" strings first, as durationDays itself takes, counts calendar dates
// instead. A still-open group has no end yet, so it's measured through today - same "start to today"
// rule the backfill wizard itself uses for an open-ended suggestion's own duration.
function groupDuration(start: Date | null, end: Date | null): string {
    if (!start) {
        return '-';
    }

    const effectiveEnd = end ?? new Date();
    return formatDuration(durationDays(format(start, 'yyyy-MM-dd'), format(effectiveEnd, 'yyyy-MM-dd')));
}

const barnSections = computed(() => store.state.sections?.filter((s) => s.barn_id === props.barn.barn_id) ?? []);

const columnFilters = ref<FacetSelection>({});
const facets = computed(() => ({
    _locationName: {
        title: i18n.global.t('views.monitoring.groupOverview.tables.barnTable.headings.location'),
        options: barnSections.value.map((section) => ({ label: section.name, value: section.id })),
    },
}));

function setLocationFilter(value: (string | number)[]) {
    const next = { ...columnFilters.value };

    if (value.length) {
        next._locationName = value;
    } else {
        delete next._locationName;
    }

    columnFilters.value = next;
}

const items = computed(() => {
    const selectedLocationIds = columnFilters.value._locationName ?? [];

    return props.barn.production_groups
        .filter((sp) => selectedLocationIds.length === 0 || selectedLocationIds.includes(sp.location_id))
        .map((sp) => ({
            ...sp,
            _locationName: getSection(sp.location_id)?.name || '',
            _duration: groupDuration(sp.start as unknown as Date | null, sp.end as unknown as Date | null),
        }));
});

// By-Section is the default until the farmer picks a mode by hand - once they do, it sticks.
const viewMode = computed<'all' | 'bySection'>(() => props.manualViewMode ?? 'bySection');

// Same natural "Section 2" < "Section 10" numeric-aware name ordering Active groups uses
// (sortSections.ts `byName`) - its live-state tiers (age/attention) don't apply here since a tile
// just states a count for the selected range, not a current status, but the name ordering itself
// should read the same across both pages.
function compareSectionNames(a: { name: string }, b: { name: string }): number {
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
}

const orderedBarnSections = computed(() => [...barnSections.value].sort(compareSectionNames));

// The Location facet-filter also narrows which Section tiles render, same selection/row-set the flat
// table already filters on - no separate filter UI for the tile grid.
const visibleSections = computed(() => {
    const selectedLocationIds = columnFilters.value._locationName ?? [];
    return selectedLocationIds.length === 0 ? orderedBarnSections.value : orderedBarnSections.value.filter((section) => selectedLocationIds.includes(section.id));
});

const itemsBySectionId = computed(() => {
    const map = new Map<number, typeof items.value>();

    for (const item of items.value) {
        const existing = map.get(item.location_id);
        if (existing) {
            existing.push(item);
        } else {
            map.set(item.location_id, [item]);
        }
    }

    return map;
});

function sectionItems(sectionId: number) {
    return itemsBySectionId.value.get(sectionId) ?? [];
}

// Location is dropped from the per-Section table - redundant once you're already looking at one.
const sectionColumns = computed(() => columns.value.filter((column) => column !== locationColumn.value));

const manuallyExpandedSectionIds = ref<Set<number>>(new Set());

function toggleSectionExpanded(sectionId: number) {
    const next = new Set(manuallyExpandedSectionIds.value);
    if (next.has(sectionId)) {
        next.delete(sectionId);
    } else {
        next.add(sectionId);
    }
    manuallyExpandedSectionIds.value = next;
}

// While a search is active, every Section with a matching row auto-expands on top of whatever the
// farmer has opened by hand, so a result can never stay hidden behind a collapsed tile.
const expandedSectionIds = computed(() => {
    if (!props.searchActive) {
        return manuallyExpandedSectionIds.value;
    }

    const expanded = new Set(manuallyExpandedSectionIds.value);
    for (const section of visibleSections.value) {
        if (sectionItems(section.id).length > 0) {
            expanded.add(section.id);
        }
    }
    return expanded;
});

const page = ref(1);
const pageSize = ref(9999);
const pageSizeOptions = ref([10, 25, 50, 100]);

const { selections, updateSelections } = inject('barnSelections') as {
    selections: Ref<Record<number, Record<number, boolean>>>;
    // eslint-disable-next-line
    updateSelections: (barnId: number, selections: Record<number, boolean>) => void
};

const rowSelection = computed({
    get() {
        return selections.value[props.barn.barn_id] || {};
    },
    set(value) {
        updateSelections(props.barn.barn_id, value);
    },
});
</script>

<template>
    <MainViewCard class="max-w-full overflow-hidden">
        <div class="divide-y divide-gray-200 space-y-4 px-4 py-4">
            <div class="flex justify-between items-center space-x-3">
                <div class="flex items-center space-x-3">
                    <div>
                        <Icon class="h-6" :src="IconSource.Location"></Icon>
                    </div>
                    <div>
                        <div class="font-bold text-lg">{{ barn.barn_name }}</div>
                        <div class="text-sm text-gray-500">
                            {{ barn.production_groups?.length || 0 }}
                            {{ barn.production_groups?.length === 1 ? $t('views.monitoring.groupOverview.texts.group') : $t('views.monitoring.groupOverview.texts.groups') }}
                        </div>
                    </div>
                </div>
                <div class="flex shrink-0 items-center gap-x-3">
                    <DataTableFacetFilter
                        v-if="viewMode === 'bySection'"
                        :title="facets._locationName.title"
                        :options="facets._locationName.options"
                        :model-value="columnFilters._locationName ?? []"
                        @update:model-value="setLocationFilter"
                    />
                    <div class="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                        <button
                            type="button"
                            class="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
                            :class="viewMode === 'bySection' ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100'"
                            @click="emit('update:manualViewMode', 'bySection')"
                        >
                            {{ $t('views.monitoring.groupOverview.fields.viewMode.bySection') }}
                        </button>
                        <button
                            type="button"
                            class="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
                            :class="viewMode === 'all' ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100'"
                            @click="emit('update:manualViewMode', 'all')"
                        >
                            {{ $t('views.monitoring.groupOverview.fields.viewMode.allGroups') }}
                        </button>
                    </div>
                    <button class="flex items-center rounded-lg gap-x-1 text-sky-500 hover:bg-sky-400/10 transition-colors px-3.5 py-2" @click="emit('backfill', barn.barn_id)">
                        <History class="size-5" aria-hidden="true" />
                        {{ $t('views.monitoring.groupOverview.buttons.backfillHistory') }}
                    </button>
                    <button class="flex items-center rounded-lg gap-x-1 text-sky-500 hover:bg-sky-400/10 transition-colors px-3.5 py-2" @click="emit('create', barn.barn_id)">
                        <Plus class="size-5" aria-hidden="true" />
                        {{ $t('views.monitoring.groupOverview.buttons.createGroup') }}
                    </button>
                </div>
            </div>
        </div>
        <DataTable
            v-if="viewMode === 'all'"
            v-model:sorting="sorting"
            v-model:row-selection="rowSelection"
            :data="items"
            :columns="columns"
            :page="page"
            :page-size="pageSize"
            :page-size-options="pageSizeOptions"
            :enable-pagination="false"
            :facets="facets"
            :column-filters="columnFilters"
            :max-body-height="MAX_BODY_HEIGHT"
            table-class="table-fixed"
            row-clickable
            @update:row-selection="updateSelections(barn.barn_id, $event)"
            @update:column-filters="columnFilters = $event"
            @row-click="emit('edit', $event)"
        >
            <template #item[name]="{ item }">
                <!-- The button is what keyboards and screen readers open; a click anywhere else on the row does the same. -->
                <button type="button" class="rounded-sm text-left font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700" @click.stop="emit('edit', item)">
                    {{ item.name }}
                </button>
            </template>
            <template #item[start]="{ item }">
                {{ item.start ? $d(item.start as any, 'short') : '-' }}
            </template>
            <template #item[end]="{ item }">
                <Badge v-if="!item.end" variant="outline" class="border-transparent bg-green-100 text-green-700">{{ $t('views.monitoring.groupOverview.tables.barnTable.ongoing') }}</Badge>
                <template v-else>{{ $d(item.end as any, 'short') }}</template>
            </template>
            <template #item[is_auto_generated]="{ item }">
                {{ item.is_auto_generated ? $t('views.monitoring.groupOverview.creationTypes.autoGenerated') : $t('views.monitoring.groupOverview.creationTypes.manuallyGenerated') }}
            </template>
            <template #item[is_favorite]="{ item }">
                <GroupFavoriteToggle :group="item" :pending="pendingFavoriteIds.has(item.id)" @toggle="toggleFavorite" />
            </template>
        </DataTable>
        <div v-else class="px-4 pb-4 space-y-3">
            <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 rounded-lg bg-gray-50 p-2">
                <button
                    v-for="section in visibleSections"
                    :key="section.id"
                    type="button"
                    class="flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors"
                    :class="expandedSectionIds.has(section.id) ? 'border-primary-700 bg-primary-700 shadow-sm' : 'border-gray-200 bg-white hover:border-primary-500 hover:bg-primary-300/30'"
                    @click="toggleSectionExpanded(section.id)"
                >
                    <span class="min-w-0">
                        <span class="block truncate font-medium" :class="expandedSectionIds.has(section.id) ? 'text-white' : 'text-gray-900'">{{ section.name }}</span>
                        <span class="block text-sm" :class="expandedSectionIds.has(section.id) ? 'text-white/80' : 'text-gray-500'">
                            {{ sectionItems(section.id).length }}
                            {{ sectionItems(section.id).length === 1 ? $t('views.monitoring.groupOverview.texts.group') : $t('views.monitoring.groupOverview.texts.groups') }}
                        </span>
                    </span>
                </button>
            </div>
            <template v-for="section in visibleSections" :key="`table-${section.id}`">
                <MainViewCard v-if="expandedSectionIds.has(section.id)" class="border-2 border-primary-500 shadow-md">
                    <div class="flex items-center justify-between gap-x-2 rounded-t-lg border-b border-primary-500 bg-primary-300/30 px-4 py-2.5">
                        <span class="flex items-center gap-x-2">
                            <span class="size-2 shrink-0 rounded-full bg-primary-700" aria-hidden="true" />
                            <span class="text-sm font-semibold text-gray-900">{{ section.name }}</span>
                        </span>
                        <button
                            type="button"
                            class="flex items-center gap-x-1 rounded-md px-2 py-1 text-sm text-sky-600 transition-colors hover:bg-sky-400/10"
                            @click="emit('backfill', barn.barn_id, section.id)"
                        >
                            <History class="size-4" aria-hidden="true" />
                            {{ $t('views.monitoring.groupOverview.buttons.backfillHistory') }}
                        </button>
                    </div>
                    <DataTable
                        v-model:sorting="getSectionTableState(section.id).sorting.value"
                        v-model:row-selection="rowSelection"
                        :data="sectionItems(section.id)"
                        :columns="sectionColumns"
                        :page="getSectionTableState(section.id).page.value"
                        :page-size="getSectionTableState(section.id).pageSize.value"
                        :page-size-options="pageSizeOptions"
                        :enable-pagination="false"
                        :max-body-height="MAX_BODY_HEIGHT"
                        table-class="table-fixed"
                        row-clickable
                        preserve-foreign-selection
                        @update:row-selection="updateSelections(barn.barn_id, $event)"
                        @row-click="emit('edit', $event)"
                    >
                        <template #item[name]="{ item }">
                            <!-- The button is what keyboards and screen readers open; a click anywhere else on the row does the same. -->
                            <button
                                type="button"
                                class="rounded-sm text-left font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
                                @click.stop="emit('edit', item)"
                            >
                                {{ item.name }}
                            </button>
                        </template>
                        <template #item[start]="{ item }">
                            {{ item.start ? $d(item.start as any, 'short') : '-' }}
                        </template>
                        <template #item[end]="{ item }">
                            <Badge v-if="!item.end" variant="outline" class="border-transparent bg-green-100 text-green-700">{{ $t('views.monitoring.groupOverview.tables.barnTable.ongoing') }}</Badge>
                            <template v-else>{{ $d(item.end as any, 'short') }}</template>
                        </template>
                        <template #item[is_auto_generated]="{ item }">
                            {{ item.is_auto_generated ? $t('views.monitoring.groupOverview.creationTypes.autoGenerated') : $t('views.monitoring.groupOverview.creationTypes.manuallyGenerated') }}
                        </template>
                        <template #item[is_favorite]="{ item }">
                            <GroupFavoriteToggle :group="item" :pending="pendingFavoriteIds.has(item.id)" @toggle="toggleFavorite" />
                        </template>
                    </DataTable>
                </MainViewCard>
            </template>
        </div>
    </MainViewCard>
</template>
